import React, { useEffect, useRef, useState } from 'react';
import { getTopCombis } from '../../../api/api';
import { TopCombiItem } from '../../../api/types';
import { useTranslation } from '../../../i18n';
import { SPORT_EMOJI } from '../DesktopLeftSidebar';

function formatOdds(val: string | number | undefined): string {
  if (val === undefined || val === null || val === '') return '-';
  const n = parseFloat(String(val));
  if (isNaN(n) || n < 1.01) return '-';
  return n.toFixed(2).replace('.', ',');
}

const SPORT_ID_TO_NAME: Record<string, string> = {
  'sr:sport:1': 'Soccer',
  'sr:sport:2': 'Basketball',
  'sr:sport:5': 'Tennis',
};

interface TopCombisProps {
  /** Adds the combi's legs to the slip as a normal accumulator. */
  onAddCombi?: (item: TopCombiItem) => void;
}

export const TopCombis: React.FC<TopCombisProps> = ({ onAddCombi }) => {
  const { locale, t } = useTranslation();
  const [items, setItems] = useState<TopCombiItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedCombi, setSelectedCombi] = useState<TopCombiItem | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    getTopCombis({ sport_ids: 'sr:sport:1,sr:sport:2,sr:sport:5', limit_per_sport: 6, preview_legs: 'all', locale })
      .then((res) => {
        if (!active) return;
        // Flatten the per-sport groups into one ranked card list.
        const flat = (res.sports || []).flatMap((s) => s.items || []);
        setItems(flat);
      })
      .catch(() => { if (active) setItems([]); })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [locale]);

  const scroll = (dir: 'left' | 'right') => {
    const el = sliderRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -330 : 330, behavior: 'smooth' });
  };

  if (!loaded || items.length === 0) return null;

  return (
    <div className="mb-5">
      <div className="flex items-end justify-between mb-2 px-2">
        <h2 className="text-[15px] font-bold text-gray-900">{t('section.topKombis')}</h2>
        <button className="text-[15px] font-bold text-gray-500 hover:text-gray-900 transition flex items-center gap-1">
          {t('section.showAll')}
          <span className="text-lg leading-none">›</span>
        </button>
      </div>

      <div className="relative w-full group/tcslider px-2">
        <button
          onClick={() => scroll('left')}
          className="absolute left-[-10px] top-1/2 -translate-y-1/2 z-20 bg-white text-gray-700 rounded-full w-10 h-10 flex items-center justify-center shadow-md border border-gray-100 opacity-0 group-hover/tcslider:opacity-100 transition-opacity hover:bg-gray-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </button>

        <div ref={sliderRef} className="flex gap-4 overflow-x-auto scrollbar-hide snap-x pb-2">
          {items.map((item) => {
            const legs = (item.selections?.length ? item.selections : item.selections_preview) || [];
            const emoji = SPORT_EMOJI[SPORT_ID_TO_NAME[item.sport_id]] || '⚽';
            const showBets = item.bets_placed_count_is_observed && (item.bets_placed_count ?? 0) > 0;
            return (
              <div
                key={item.combi_id}
                className="snap-start flex-shrink-0 w-[310px] p-3.5 flex flex-col border border-gray-200 rounded-xl hover:shadow-md transition bg-white"
              >
                {/* Header — same slanted red style as Top Events cards */}
                <div className="relative -mx-3.5 -mt-3.5 mb-3 h-[40px] flex items-center justify-between px-3.5">
                  {/* Mobile Background */}
                  <div className="absolute top-0 left-0 h-full w-[60%] rounded-tl-xl md:hidden" style={{ backgroundImage: 'linear-gradient(rgb(156, 9, 41) 0%, rgb(57, 7, 18) 100%)', clipPath: 'polygon(0 0, 100% 0, calc(100% - 15px) 100%, 0 100%)' }} />
                  <div className="absolute top-0 left-[calc(60%+3px)] h-full w-[4px] transform -skew-x-[22deg] origin-bottom md:hidden" style={{ backgroundImage: 'linear-gradient(rgb(156, 9, 41) 0%, rgb(57, 7, 18) 100%)' }} />
                  
                  {/* Desktop Background */}
                  <div className="absolute top-0 left-0 h-full w-[60%] rounded-tl-xl hidden md:block" style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)', clipPath: 'polygon(0 0, 100% 0, calc(100% - 15px) 100%, 0 100%)' }} />
                  <div className="absolute top-0 left-[calc(60%+3px)] h-full w-[4px] transform -skew-x-[22deg] origin-bottom hidden md:block" style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)' }} />
                  <div className="flex items-center gap-1.5 text-white relative z-10 pl-1">
                    <span className="text-[14px]">{emoji}</span>
                    <span className="text-[14px] font-bold tracking-wide">{item.fold_size}er-Kombi</span>
                  </div>
                  {showBets && (
                    <div className="text-[12px] font-bold text-[#00a650] relative z-10 pr-1">
                      {item.bets_placed_count}× getippt
                    </div>
                  )}
                </div>

                {/* Legs */}
                <div className="flex flex-col gap-2 mb-3 flex-1">
                  {legs.map((sel, i) => (
                    <div key={`${sel.event_id}-${sel.market_id}-${sel.outcome_id}-${i}`} className="flex items-start gap-2">
                      <span className="mt-[3px] w-[8px] h-[8px] rounded-full bg-[#c8102e] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] text-gray-500 truncate leading-snug">{sel.event_name}</div>
                        <div className="text-[13px] leading-snug truncate">
                          <span className="text-gray-500">{sel.market_name}: </span>
                          <span className="font-bold text-gray-900">{sel.outcome_name}</span>
                        </div>
                      </div>
                      {sel.odds && <span className="text-[13px] font-bold text-gray-800 flex-shrink-0">{formatOdds(sel.odds)}</span>}
                    </div>
                  ))}
                </div>

                {/* Footer: add button + total odds */}
                <div className="flex items-stretch gap-2">
                  <button
                    onClick={() => setSelectedCombi(item)}
                    disabled={!onAddCombi}
                    className="flex-1 text-white text-[14px] font-bold rounded-md py-2 transition disabled:opacity-50 disabled:cursor-not-allowed md:hidden block"
                    style={{ backgroundImage: 'linear-gradient(rgb(156, 9, 41) 0%, rgb(57, 7, 18) 100%)' }}
                  >
                    Wettschein ansehen
                  </button>
                  <button
                    onClick={() => setSelectedCombi(item)}
                    disabled={!onAddCombi}
                    className="flex-1 text-white text-[14px] font-bold rounded-md py-2 transition disabled:opacity-50 disabled:cursor-not-allowed hidden md:block"
                    style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)' }}
                  >
                    Wettschein ansehen
                  </button>
                  <div className="min-w-[72px] bg-[#f0f2f5] rounded-md flex items-center justify-center px-3 text-[15px] font-black text-gray-900">
                    {formatOdds(item.total_odds)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <button
          onClick={() => scroll('right')}
          className="absolute right-[-10px] top-1/2 -translate-y-1/2 z-20 bg-white text-gray-700 rounded-full w-10 h-10 flex items-center justify-center shadow-md border border-gray-100 opacity-0 group-hover/tcslider:opacity-100 transition-opacity hover:bg-gray-50"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {selectedCombi && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedCombi(null)}>
          <div className="bg-white rounded-[20px] w-full max-w-[360px] flex flex-col overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div 
              className="relative py-4 border-b border-gray-100 flex items-center justify-center md:hidden"
              style={{ backgroundImage: 'linear-gradient(rgb(156, 9, 41) 0%, rgb(57, 7, 18) 100%)' }}
            >
              <h3 className="text-[17px] font-black text-white">Kombi #{items.findIndex(i => i.combi_id === selectedCombi.combi_id) + 1}</h3>
              <button 
                onClick={() => setSelectedCombi(null)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/20 border border-white/30 rounded-full text-white hover:bg-white/30 transition shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div 
              className="relative py-4 border-b border-gray-100 items-center justify-center hidden md:flex"
              style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)' }}
            >
              <h3 className="text-[17px] font-black text-white">Kombi #{items.findIndex(i => i.combi_id === selectedCombi.combi_id) + 1}</h3>
              <button 
                onClick={() => setSelectedCombi(null)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-white/20 border border-white/30 rounded-full text-white hover:bg-white/30 transition shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            {/* Modal Stats */}
            <div className="flex justify-between items-center px-4 py-5 border-b border-gray-100 bg-[#f8f9fa]">
              <div className="text-center flex-1">
                <div className="text-[18px] font-black text-[#00a650] flex items-center justify-center gap-1">
                  {selectedCombi.bets_placed_count || 0}
                  {selectedCombi.bets_placed_count && selectedCombi.bets_placed_count > 0 && (
                    <svg className="w-3 h-3 text-[#00a650]" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
                  )}
                </div>
                <div className="text-[11px] font-bold text-gray-500 tracking-wide mt-0.5">Wetten platziert</div>
              </div>
              <div className="w-[1px] h-10 bg-gray-200"></div>
              <div className="text-center flex-1">
                <div className="text-[18px] font-black text-gray-900">{selectedCombi.fold_size}x</div>
                <div className="text-[11px] font-bold text-gray-500 tracking-wide mt-0.5">Kombi</div>
              </div>
              <div className="w-[1px] h-10 bg-gray-200"></div>
              <div className="text-center flex-1">
                <div className="text-[18px] font-black text-gray-900">{formatOdds(selectedCombi.total_odds)}</div>
                <div className="text-[11px] font-bold text-gray-500 tracking-wide mt-0.5">Gesamtquote</div>
              </div>
            </div>

            {/* Modal Legs */}
            <div className="flex flex-col overflow-y-auto max-h-[50vh] p-4 bg-white">
              {((selectedCombi.selections?.length ? selectedCombi.selections : selectedCombi.selections_preview) || []).map((sel, i) => {
                let dateStr = '';
                if (sel.scheduled_at) {
                  const d = new Date(sel.scheduled_at);
                  const isToday = new Date().toDateString() === d.toDateString();
                  const isTomorrow = new Date(Date.now() + 86400000).toDateString() === d.toDateString();
                  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
                  if (isToday) dateStr = `heute, ${time}`;
                  else if (isTomorrow) dateStr = `morgen, ${time}`;
                  else dateStr = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
                }

                return (
                  <div key={`${sel.event_id}-${sel.market_id}-${sel.outcome_id}-${i}`} className="flex flex-col border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5 min-w-0 pr-2">
                        <span className="text-[15px]">{SPORT_EMOJI[SPORT_ID_TO_NAME[selectedCombi.sport_id]] || '⚽'}</span>
                        <span className="text-[14px] text-gray-500 font-medium shrink-0">Tipp:</span>
                        <span className="text-[14px] font-bold text-gray-900 truncate">{sel.outcome_name}</span>
                      </div>
                      <span className="text-[15px] font-black text-gray-900 shrink-0">{formatOdds(sel.odds)}</span>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <div className="w-[15px] shrink-0" />
                      <div className="flex flex-col">
                        <div className="text-[13px] text-gray-600 leading-snug">
                          {sel.event_name}
                        </div>
                        {dateStr && (
                          <div className="text-[12px] text-gray-400 mt-0.5">
                            {dateStr}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-white border-t border-gray-100">
              <button
                onClick={() => {
                  onAddCombi?.(selectedCombi);
                  setSelectedCombi(null);
                }}
                className="w-full text-white text-[15px] font-bold rounded-xl py-3.5 transition shadow-md md:hidden block"
                style={{ backgroundImage: 'linear-gradient(rgb(156, 9, 41) 0%, rgb(57, 7, 18) 100%)' }}
              >
                Wettschein hinzufügen
              </button>
              <button
                onClick={() => {
                  onAddCombi?.(selectedCombi);
                  setSelectedCombi(null);
                }}
                className="w-full text-white text-[15px] font-bold rounded-xl py-3.5 transition shadow-md hidden md:block"
                style={{ backgroundImage: 'var(--bg-brand-gradient-vertical)' }}
              >
                Wettschein hinzufügen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
